import {TransferType, useAllFieldsQuery} from '@transactions/graphql';
import {isCategoryColor, isCategoryIcon} from 'common/categoryAppearance';
import Money from 'common/Money';
import {ArrowDownLeft, ArrowLeftRight, ReceiptText} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AccountAvatar} from 'src/AccountAvatar';
import {AmountDisplay} from 'src/AmountDisplay';
import {categoryTintClasses} from 'src/categoryAppearance';
import {CategoryAvatar} from 'src/CategoryAvatar';
import {CategoryInlineDisplay} from 'src/CategoryInlineDisplay';
import CheckboxBase from 'src/Checkbox/Base';
import {useAccountsById} from 'src/hooks/useAccountsById';
import {useLongPress} from 'src/hooks/useLongPress';
import {MultiFieldDisplay} from 'src/inputs/FieldInput';
import {formatListDate} from 'src/lib/displayDate';
import {LogoImage} from 'src/LogoImage';
import {TransactionLines} from 'src/TransactionListView/TransactionLines';
import type {
  AccountListItemFragment,
  UnifiedTransactionFragment,
} from '@transactions/graphql';

type Props = {
  onSelect: (selected: boolean) => void;
  onShiftSelect?: () => void;
  selected: boolean;
  isSelectMode: boolean;
  transaction: UnifiedTransactionFragment;
};

const transferLabels: Record<TransferType, string> = {
  [TransferType.AccountTransfer]: 'Transfer',
  [TransferType.CreditCardPayment]: 'Card payment',
  [TransferType.Prepaid]: 'Prepaid',
  [TransferType.Refund]: 'Refund',
  [TransferType.Reimbursement]: 'Reimbursement',
  [TransferType.Conversion]: 'Conversion',
};

const Avatar = (props: {transaction: UnifiedTransactionFragment}) => {
  const {transaction} = props;
  const base =
    'flex size-9 max-md:size-8 shrink-0 items-center justify-center rounded-full';
  const {category} = transaction;

  // The payee's own logo beats any category icon or the transfer arrows: it
  // identifies the merchant specifically, where the others only say what
  // kind of thing it was. A transfer only has one via its optional payee —
  // a refund's merchant, a reimbursement's payer — and the type badge on
  // the row still marks it as a transfer.
  if (transaction.logo != null) {
    return (
      <LogoImage
        logo={transaction.logo}
        className="size-9 max-md:size-8"
        // Rounded square, not a circle, matching every other logo in the app
        // (accounts, breadcrumbs, the picker). Most of these are App Store
        // icons drawn as squircles, so a circular crop also cuts into marks
        // that were designed to reach the corners.
        //
        // rounded-md is 8px against this 36px tile — the ~0.22 ratio Apple
        // draws icons for. rounded-lg resolves to 10px here (the theme sets
        // --radius: 0.625rem) and reads noticeably rounder than the artwork.
        rounded="rounded-md"
        alt={transaction.payee ?? ''}
      />
    );
  }
  if (transaction.netAmount === 0) {
    return (
      <div className={`${base} bg-muted text-muted-foreground`}>
        <ArrowLeftRight className="size-4" />
      </div>
    );
  }
  // An explicitly chosen category icon wins even over the income arrow —
  // the green amount already signals direction.
  if (category?.icon != null && isCategoryIcon(category.icon)) {
    return <CategoryAvatar category={category} />;
  }
  if (transaction.netAmount > 0) {
    // Icon-less income keeps the inflow arrow, but an explicitly chosen
    // category color still tints it (green otherwise).
    const tint =
      category?.color != null && isCategoryColor(category.color)
        ? categoryTintClasses[category.color]
        : 'bg-positive/15 text-positive';
    return (
      <div className={`${base} ${tint}`}>
        <ArrowDownLeft className="size-4" />
      </div>
    );
  }
  if (category) {
    return <CategoryAvatar category={category} />;
  }
  return (
    <div className={`${base} bg-muted text-muted-foreground`}>
      <ReceiptText className="size-4" />
    </div>
  );
};

const TransactionRow = (props: Props) => {
  const {transaction, selected, onSelect, onShiftSelect, isSelectMode} = props;
  const {data: fieldsData} = useAllFieldsQuery();
  const fields = fieldsData?.allFields || [];
  const accountsById = useAccountsById();

  const isBalanced = transaction.netAmount === 0;

  const accounts = React.useMemo(() => {
    const ids = transaction.accountIds ?? [];
    return ids
      .map((id) => accountsById.get(id))
      .filter((account): account is AccountListItemFragment => account != null);
  }, [transaction.accountIds, accountsById]);

  const {longPressProps, consumeLongPress} = useLongPress(() =>
    onSelect(true),
  );

  const handleClick = (event: React.MouseEvent) => {
    // If long-press just triggered, prevent navigation and ignore
    if (consumeLongPress()) {
      event.preventDefault();
      return;
    }

    // In select mode: prevent navigation, toggle selection
    if (isSelectMode) {
      event.preventDefault();
      if (event.shiftKey && onShiftSelect) {
        onShiftSelect();
      } else {
        onSelect(!selected);
      }
      return;
    }
  };

  // A transfer's payee (its counterparty) identifies the row better than the
  // free-text description, so it takes the title when present — mirroring
  // payment rows — and the description falls back in when there is none.
  const title = isBalanced
    ? transaction.payee || transaction.description
    : transaction.payee;

  // For transfers the net is zero by definition; the interesting number is
  // how much moved, i.e. the inflow side of the consumptions. A conversion's
  // legs are in two currencies the fragment can't distinguish, so no single
  // moved amount exists — fall through to the "—" placeholder.
  const movedAmount =
    transaction.transferType === TransferType.Conversion
      ? 0
      : transaction.consumptions
          .filter((consumption) => consumption.amount > 0)
          .reduce((sum, consumption) => sum + consumption.amount, 0);

  // A refund or reimbursement is money crossing back over the household
  // boundary — the inflow reads like income, so it gets the payment rows'
  // treatment (full weight, green positive) rather than the muted movement
  // number the own-account transfer kinds keep.
  const isCounterpartyTransfer =
    transaction.transferType === TransferType.Refund ||
    transaction.transferType === TransferType.Reimbursement;

  // Foreign-currency payments show their USD value beneath the native
  // amount. homeNetAmount equals netAmount for USD transactions, so only a
  // genuinely foreign row gets the second line.
  const approxHome =
    transaction.currency !== 'USD' && transaction.homeNetAmount != null
      ? Money.fromInt(transaction.homeNetAmount, 'USD')
      : null;

  // The meta line shows the dimension that defines the row: a transfer is an
  // account movement, so it gets its accounts (with their avatars); a payment
  // is a categorized purchase, so it gets its category and the raw
  // description. Payments deliberately do not name their account — it repeats
  // the same card on nearly every row, and the account filter, ledger, and
  // detail page all answer that question.
  const {category, description} = transaction;
  const hasDescription = description != null && description !== '';
  // When the payee holds a transfer's title, the description would otherwise
  // vanish from the row — so it joins the meta line after the account chain,
  // mirroring payment rows (structural part first, free text last).
  const transferShowsDescription =
    isBalanced && Boolean(transaction.payee) && hasDescription;
  const metaTitle = isBalanced
    ? [
        accounts.map((account) => account.name).join(' → ') || null,
        transferShowsDescription ? description : null,
      ]
        .filter((part): part is string => part != null)
        .join(' · ')
    : [category?.name, hasDescription ? description : null]
        .filter((part): part is string => part != null)
        .join(' · ');

  const hasChips =
    transaction.tags.length > 0 ||
    Object.values(transaction.fieldValues || {}).some(
      (value) => value != null && value !== '',
    );

  // A transaction made of several lines can open them in place, beneath
  // the row (which then leaves its bottom border to them). A transfer's two
  // legs are already on the row as its accounts, so only an unusual
  // transfer — three or more legs — gets the toggle.
  const lineCount = new Set(
    transaction.consumptions.map((consumption) => consumption.rawTransactionId),
  ).size;
  const hasLines = lineCount > (isBalanced ? 2 : 1);

  return (
    <>
    <Link
      to={`/transactions/${transaction._id}`}
      onClick={handleClick}
      {...longPressProps}
      className={`${
        selected ? 'bg-accent' : 'bg-transparent'
      } hover:bg-accent grid ${
        isSelectMode
          ? '[grid-template-columns:max-content_minmax(0,1fr)_max-content]'
          : '[grid-template-columns:minmax(0,1fr)_max-content]'
      } gap-x-5 max-md:gap-x-2 items-center px-4 py-3 max-md:px-3 box-border w-full ${
        hasLines ? '' : 'border-b border-border'
      } select-none`}
    >
      {isSelectMode && (
        // pointer-events-none: the row's own click handler toggles selection
        // and prevents link navigation; the checkbox is display-only.
        <div className="pointer-events-none flex items-center self-center pr-1">
          <CheckboxBase value={selected ? 'check' : 'empty'} />
        </div>
      )}
      <div className="flex min-w-0 items-center gap-3 max-md:gap-2.5">
        <Avatar transaction={transaction} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span
              className="text-foreground truncate font-medium"
              title={title ?? ''}
            >
              {title}
            </span>
            {transaction.pending && (
              <span className="bg-warning/20 text-warning rounded px-1.5 py-0.5 text-xs font-medium shrink-0">
                Pending
              </span>
            )}
            {isBalanced && (
              <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-medium shrink-0">
                {transaction.transferType
                  ? transferLabels[transaction.transferType]
                  : 'Transfer'}
              </span>
            )}
          </div>
          {metaTitle && (
            <div
              className="text-muted-foreground flex items-center gap-1.5 overflow-hidden text-sm"
              title={metaTitle}
            >
              {isBalanced ? (
                <>
                  {accounts.map((account, index) => (
                    <React.Fragment key={account._id}>
                      {index > 0 && <span className="shrink-0">→</span>}
                      <span className="flex min-w-0 items-center gap-1">
                        <AccountAvatar
                          account={account}
                          className="size-3.5 rounded-[3px]"
                        />
                        <span className="truncate">{account.name}</span>
                      </span>
                    </React.Fragment>
                  ))}
                  {transferShowsDescription && (
                    <>
                      {accounts.length > 0 && (
                        <span className="shrink-0">·</span>
                      )}
                      {/* Free text is the least interesting part, so it
                          alone truncates when the line runs out of room. */}
                      <span className="truncate">{description}</span>
                    </>
                  )}
                </>
              ) : (
                <>
                  {category && <CategoryInlineDisplay category={category} />}
                  {category && hasDescription && (
                    <span className="shrink-0">·</span>
                  )}
                  {hasDescription && (
                    // The raw bank string is the least interesting part, so
                    // it alone truncates when the line runs out of room.
                    <span className="truncate">{description}</span>
                  )}
                </>
              )}
            </div>
          )}
          {hasChips && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {transaction.tags.map((tag) => (
                <span
                  key={tag._id}
                  className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium"
                >
                  {tag.name}
                </span>
              ))}
              <MultiFieldDisplay
                fields={fields}
                fieldValues={transaction.fieldValues || {}}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-col items-end justify-center gap-0.5 text-right">
        {isBalanced ? (
          isCounterpartyTransfer && movedAmount > 0 ? (
            <div className="font-medium">
              <AmountDisplay
                amount={Money.fromInt(movedAmount, transaction.currency)}
              />
            </div>
          ) : (
            <div className="text-muted-foreground">
              {movedAmount > 0 ? (
                <AmountDisplay
                  variant="plain"
                  amount={Money.fromInt(movedAmount, transaction.currency)}
                />
              ) : (
                <span>—</span>
              )}
            </div>
          )
        ) : (
          <div className="font-medium">
            <AmountDisplay
              amount={Money.fromInt(
                transaction.netAmount,
                transaction.currency,
              )}
              approx={approxHome}
            />
          </div>
        )}
        <span className="text-muted-foreground text-sm">
          {formatListDate(transaction.date)}
        </span>
      </div>
    </Link>
    {hasLines && (
      <TransactionLines
        transactionId={transaction._id}
        lineCount={lineCount}
        currency={transaction.currency}
      />
    )}
    </>
  );
};

export default TransactionRow;
